import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  UploadCloud, FileSpreadsheet, Loader2, CheckCircle2, XCircle, AlertTriangle,
  Download, RotateCcw, Leaf, ChevronDown, ChevronRight, Play, Users,
} from "lucide-react";
import { API } from "../../App";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";

const getAuth = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

const PHASE_LABELS = {
  starting: "Preparing import…",
  customers: "Importing customers…",
  jobs: "Importing jobs…",
  finalizing: "Finalizing…",
  done: "Done",
};

const CHUNK_SIZE = 4 * 1024 * 1024;

export default function JobberImportCard() {
  const [session, setSession] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [history, setHistory] = useState([]);
  const fileRef = useRef(null);
  const pollRef = useRef(null);

  const loadHistory = async () => {
    try {
      const res = await axios.get(`${API}/import/jobber/sessions`, getAuth());
      setHistory(res.data?.sessions || []);
      return res.data?.sessions || [];
    } catch { return []; }
  };

  useEffect(() => {
    (async () => {
      const sessions = await loadHistory();
      const latest = sessions[0];
      if (latest && ["running", "preview"].includes(latest.status)) {
        try {
          const full = await axios.get(`${API}/import/jobber/${latest.session_id}`, getAuth());
          setSession(full.data);
          if (full.data.status === "running") startPolling(latest.session_id);
        } catch { /* ignore */ }
      }
    })();
    return () => clearInterval(pollRef.current);
  }, []);

  const openSession = async (sessionId) => {
    try {
      const res = await axios.get(`${API}/import/jobber/${sessionId}`, getAuth());
      setSession(res.data);
      if (res.data.status === "running") startPolling(sessionId);
    } catch {
      toast.error("Could not load import session");
    }
  };

  const startPolling = (sessionId) => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/import/jobber/${sessionId}`, getAuth());
        setSession(res.data);
        if (["completed", "failed"].includes(res.data?.status)) {
          clearInterval(pollRef.current);
          loadHistory();
          if (res.data.status === "completed") toast.success("Jobber import completed!");
          else toast.error(`Import failed: ${res.data.error || "unknown error"}`);
        }
      } catch { /* keep polling */ }
    }, 2000);
  };

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setUploading(true);
    setUploadPct(0);
    try {
      const uploadId = `up${Date.now()}${Math.random().toString(36).slice(2, 8)}`;
      const totalBytes = files.reduce((s, f) => s + f.size, 0) || 1;
      let sent = 0;
      const manifest = [];
      for (const file of files) {
        const totalChunks = Math.max(1, Math.ceil(file.size / CHUNK_SIZE));
        for (let i = 0; i < totalChunks; i++) {
          const slice = file.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
          const form = new FormData();
          form.append("upload_id", uploadId);
          form.append("filename", file.name);
          form.append("chunk_index", String(i));
          form.append("total_chunks", String(totalChunks));
          form.append("chunk", slice, file.name);
          await axios.post(`${API}/import/jobber/upload-chunk`, form, getAuth());
          sent += slice.size;
          setUploadPct(Math.min(99, Math.round((sent / totalBytes) * 100)));
        }
        manifest.push({ filename: file.name, total_chunks: totalChunks });
      }
      const res = await axios.post(`${API}/import/jobber/upload-complete`, {
        upload_id: uploadId,
        files: manifest,
      }, getAuth());
      setSession(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed — please try again");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const startImport = async () => {
    try {
      await axios.post(`${API}/import/jobber/${session.session_id}/commit`, {}, getAuth());
      setSession(s => ({ ...s, status: "running", progress: { phase: "starting" } }));
      startPolling(session.session_id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start import");
    }
  };

  const discard = async () => {
    try {
      await axios.post(`${API}/import/jobber/${session.session_id}/cancel`, {}, getAuth());
    } catch { /* best effort */ }
    setSession(null);
    loadHistory();
  };

  const downloadErrors = async (sessionId) => {
    try {
      const res = await axios.get(`${API}/import/jobber/${sessionId}/errors.csv`, {
        ...getAuth(), responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `jobber-import-errors-${sessionId}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download error report");
    }
  };

  return (
    <>
      <Card data-testid="jobber-import-card">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Leaf className="w-4 h-4 text-emerald-500" /> Import from Jobber
          </CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Upload your Jobber exports — the Clients export (CSV) and job reports (One-Off Jobs,
            Recurring Jobs). Upload a .zip or multiple files at once. Clients sharing a real phone
            number AND a similar name are merged into one customer with all addresses kept; lead
            sources become Job Sources; tags, custom fields and archived status are preserved.
            Re-uploading never duplicates records.
          </p>
        </CardHeader>
        <CardContent>
          {!session && (
            <div
              className="border-2 border-dashed border-border rounded-xl p-8 text-center cursor-pointer hover:border-emerald-500/60 transition-colors"
              onClick={() => fileRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); handleFiles(e.dataTransfer.files); }}
              data-testid="jobber-dropzone"
            >
              <input
                ref={fileRef} type="file" multiple hidden
                accept=".zip,.csv,.xls,.xlsx"
                onChange={(e) => handleFiles(e.target.files)}
                data-testid="jobber-file-input"
              />
              {uploading ? (
                <div className="flex flex-col items-center gap-2">
                  <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
                  <p className="text-sm text-muted-foreground" data-testid="jobber-upload-progress">
                    Uploading… {uploadPct}%
                  </p>
                  <div className="w-56 h-1.5 bg-border rounded-full overflow-hidden">
                    <div className="h-full bg-emerald-500 transition-all" style={{ width: `${uploadPct}%` }} />
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <UploadCloud className="w-8 h-8 text-muted-foreground" />
                  <p className="text-sm text-foreground font-medium">
                    Drop your Jobber export here, or click to browse
                  </p>
                  <p className="text-xs text-muted-foreground">.zip, .csv, .xls, .xlsx — multiple files supported, large files upload in chunks</p>
                </div>
              )}
            </div>
          )}

          {session && session.status === "preview" && (
            <PreviewStep session={session} onStart={startImport} onDiscard={discard} />
          )}

          {session && session.status === "running" && <RunningStep session={session} />}

          {session && ["completed", "failed"].includes(session.status) && (
            <ResultStep
              session={session}
              onDownloadErrors={() => downloadErrors(session.session_id)}
              onReset={() => { setSession(null); loadHistory(); }}
            />
          )}
        </CardContent>
      </Card>

      {history.length > 0 && (
        <Card data-testid="jobber-history-card">
          <CardHeader>
            <CardTitle className="text-base">Jobber import history</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {history.map(h => (
                <div key={h.session_id} className="flex items-center justify-between gap-3 border border-border rounded-lg px-3 py-2 flex-wrap" data-testid={`jobber-history-${h.session_id}`}>
                  <div className="min-w-0">
                    <div className="text-sm text-foreground">
                      {new Date(h.created_at).toLocaleString()}
                      <span className="text-muted-foreground ml-2 text-xs">
                        {(h.files || []).filter(f => f.file_type).length} file(s)
                      </span>
                    </div>
                    {h.counts && (
                      <div className="text-xs text-muted-foreground mt-0.5">
                        {h.counts.customers?.created ?? 0} customers · {h.counts.addresses?.created ?? 0} addresses · {h.counts.jobs?.created ?? 0} jobs · {h.counts.errors ?? 0} errors
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <StatusBadge status={h.status} />
                    <Button
                      size="sm" variant="ghost"
                      onClick={() => openSession(h.session_id)}
                      data-testid={`jobber-view-session-${h.session_id}`}
                    >
                      {h.status === "running" ? "View progress" : "View"}
                    </Button>
                    {(h.errors_count || 0) > 0 && ["completed", "failed"].includes(h.status) && (
                      <Button size="sm" variant="ghost" onClick={() => downloadErrors(h.session_id)} title="Download error report">
                        <Download className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </>
  );
}

function StatusBadge({ status }) {
  const map = {
    completed: "bg-emerald-500/15 text-emerald-500 border-emerald-500/30",
    running: "bg-blue-500/15 text-blue-400 border-blue-500/30",
    preview: "bg-amber-500/15 text-amber-500 border-amber-500/30",
    failed: "bg-red-500/15 text-red-500 border-red-500/30",
    cancelled: "bg-gray-500/15 text-gray-400 border-gray-500/30",
  };
  return <Badge variant="outline" className={`${map[status] || map.cancelled} capitalize`}>{status}</Badge>;
}

function RunningStep({ session }) {
  const done = session.progress?.done ?? null;
  const total = session.progress?.total ?? null;
  const pct = total ? Math.min(100, Math.round(((done || 0) / total) * 100)) : null;
  return (
    <div className="flex flex-col items-center gap-3 py-8" data-testid="jobber-import-progress">
      <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      <p className="text-sm text-foreground font-medium">
        {PHASE_LABELS[session.progress?.phase] || "Importing…"}
      </p>
      {pct !== null && (
        <>
          <div className="w-72 max-w-full h-2 bg-border rounded-full overflow-hidden">
            <div
              className="h-full bg-emerald-500 transition-all duration-500"
              style={{ width: `${pct}%` }}
              data-testid="jobber-progress-bar"
            />
          </div>
          <p className="text-xs text-muted-foreground" data-testid="jobber-progress-counts">
            {(done || 0).toLocaleString()} of {total.toLocaleString()} records · {pct}%
          </p>
        </>
      )}
      {session.progress?.detail && pct === null && (
        <p className="text-xs text-muted-foreground">{session.progress.detail}</p>
      )}
      <p className="text-xs text-muted-foreground text-center">
        You can leave this page or refresh — the import keeps running in the background.
      </p>
    </div>
  );
}

function PreviewStep({ session, onStart, onDiscard }) {
  const detected = (session.files || []).filter(f => f.file_type);
  const skipped = (session.files || []).filter(f => !f.file_type);
  const cp = session.client_preview;
  return (
    <div className="space-y-4" data-testid="jobber-preview">
      <div className="text-sm text-foreground font-medium">
        {detected.length} file(s) recognized — review before importing. Nothing has been written yet.
      </div>

      {cp && (
        <div className="border border-emerald-500/25 bg-emerald-500/5 rounded-lg p-3" data-testid="jobber-merge-summary">
          <div className="text-xs font-medium text-emerald-500 flex items-center gap-1.5 mb-2">
            <Users className="w-3.5 h-3.5" /> Client merge preview
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <MergeStat label="Rows in file(s)" value={cp.rows} />
            <MergeStat label="Unique Jobber clients" value={cp.unique_clients} />
            <MergeStat label="Customers after merge" value={cp.customers_after_merge} highlight />
            <MergeStat label="Merged (same phone + name)" value={cp.phone_merged_groups} />
            <MergeStat label="Addresses kept" value={cp.total_addresses} />
            <MergeStat label="Archived in Jobber" value={cp.archived} />
            <MergeStat label="Without a phone" value={cp.no_phone} />
          </div>
          <p className="text-[11px] text-muted-foreground mt-2">
            Clients are merged only when they share a real phone number AND a similar name — different
            businesses on the same line stay separate. Every address, phone, email, tag and custom field is kept.
          </p>
        </div>
      )}

      <div className="space-y-2">
        {detected.map(f => <FilePreviewRow key={f.file_id} file={f} />)}
        {skipped.map(f => (
          <div key={f.file_id} className="border border-red-500/30 rounded-lg p-3 flex items-start gap-2">
            <XCircle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
            <div>
              <div className="text-sm text-foreground">{f.filename}</div>
              <div className="text-xs text-red-400">{f.error}</div>
            </div>
          </div>
        ))}
      </div>
      <div className="flex gap-3 pt-2">
        <Button onClick={onStart} className="btn-primary" data-testid="jobber-start-import-btn" disabled={detected.length === 0}>
          <Play className="w-4 h-4 mr-1" /> Start import
        </Button>
        <Button variant="outline" onClick={onDiscard} data-testid="jobber-discard-btn">Discard</Button>
      </div>
    </div>
  );
}

function MergeStat({ label, value, highlight }) {
  return (
    <div className={`rounded-lg px-2.5 py-1.5 border ${highlight ? "border-emerald-500/40 bg-emerald-500/10" : "border-border"}`}>
      <div className="text-base font-bold text-foreground">{(value ?? 0).toLocaleString()}</div>
      <div className="text-[10px] text-muted-foreground leading-tight">{label}</div>
    </div>
  );
}

function FilePreviewRow({ file }) {
  const [open, setOpen] = useState(false);
  const sample = file.sample || [];
  const cols = sample.length ? Object.keys(sample[0]).slice(0, 6) : [];
  return (
    <div className="border border-border rounded-lg p-3" data-testid={`jobber-file-${file.file_id}`}>
      <button className="w-full flex items-center justify-between gap-2 text-left" onClick={() => setOpen(o => !o)}>
        <div className="flex items-center gap-2 min-w-0">
          <FileSpreadsheet className="w-4 h-4 text-emerald-500 shrink-0" />
          <span className="text-sm text-foreground truncate">{file.filename}</span>
          <Badge variant="outline" className="text-xs shrink-0">{file.type_label}</Badge>
        </div>
        <div className="flex items-center gap-2 shrink-0 text-xs text-muted-foreground">
          {file.row_count} rows
          {file.parse_errors > 0 && (
            <span className="text-amber-500 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" /> {file.parse_errors} skipped
            </span>
          )}
          {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </div>
      </button>
      {open && sample.length > 0 && (
        <div className="mt-3 overflow-x-auto">
          <table className="text-xs w-full">
            <thead>
              <tr className="text-muted-foreground border-b border-border">
                {cols.map(c => <th key={c} className="text-left py-1 pr-4 font-medium whitespace-nowrap">{c}</th>)}
              </tr>
            </thead>
            <tbody>
              {sample.map((row, i) => (
                <tr key={i} className="border-b border-border/40">
                  {cols.map(c => (
                    <td key={c} className="py-1 pr-4 text-foreground whitespace-nowrap max-w-[220px] truncate">
                      {String(row[c] ?? "")}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ResultStep({ session, onDownloadErrors, onReset }) {
  const c = session.counts || {};
  const failed = session.status === "failed";
  const stats = [
    ["Customers created", c.customers?.created],
    ["Customers merged", c.customers?.merged],
    ["Customers updated", c.customers?.updated],
    ["Customers from job refs", c.customers?.created_from_refs],
    ["Addresses added", c.addresses?.created],
    ["Job sources created", c.job_sources?.created],
    ["Jobs imported", c.jobs?.created],
    ["Jobs updated", c.jobs?.updated],
  ].filter(([, v]) => (v ?? 0) > 0);

  return (
    <div className="space-y-4" data-testid="jobber-result">
      <div className="flex items-center gap-2">
        {failed ? <XCircle className="w-5 h-5 text-red-500" /> : <CheckCircle2 className="w-5 h-5 text-emerald-500" />}
        <span className="text-sm font-medium text-foreground">
          {failed ? "Import failed — created records were rolled back." : "Import completed."}
        </span>
      </div>
      {failed && session.error && <p className="text-xs text-red-400">{session.error}</p>}
      {stats.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {stats.map(([label, value]) => (
            <div key={label} className="border border-border rounded-lg px-3 py-2">
              <div className="text-lg font-bold text-foreground">{value}</div>
              <div className="text-xs text-muted-foreground">{label}</div>
            </div>
          ))}
        </div>
      )}
      <div className="flex flex-wrap gap-3">
        {(session.errors_count || 0) > 0 && (
          <Button variant="outline" onClick={onDownloadErrors} data-testid="jobber-download-errors-btn">
            <Download className="w-4 h-4 mr-1" /> Download error report ({session.errors_count})
          </Button>
        )}
        <Button variant="outline" onClick={onReset} data-testid="jobber-import-more-btn">
          <RotateCcw className="w-4 h-4 mr-1" /> Import more files
        </Button>
      </div>
    </div>
  );
}
